import { useState } from "react";
import { Row, Col, Button, Form } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";
import { useDispatch } from "react-redux";
import CustomBadge from "@/components/desktop/badge/CustomBadge";
import Select from "react-select";

//CKEditor5
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import DOMPurify from "dompurify";

//Services
import { dateToLocale } from "@/services/Utils";
import api from "@/services/api.service";
import { SAVE_UPDATE_SUCCESS, DELETE_UPDATE_SUCCESS, INITIAL_UPDATE_REQUEST } from "@/redux/types/updates.types";

//Styles
import classes from "../updates.module.scss";

//Icons
import { ReactComponent as CloseIcon } from "@/assets/images/icons/Updates/close-icon.svg";
import { ReactComponent as LinkedInSvg } from "@/assets/images/icons/Updates/linkedin-brands.svg";
import { ReactComponent as WhatsappSvg } from "@/assets/images/icons/Updates/whatsapp-brands.svg";

//Components
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import Fetching from "@/components/Fetching/Fetching";

function uploadAdapter(loader) {
	return {
		upload: async () => {
			const body = new FormData();
			const file = await loader.file;
			body.append("files[]", file);
			const response = await api.post("/admin/upload_images", body);
			return {
				default: response.public_url ?? response.url,
			};
		},
	};
}

function uploadPlugin(editor) {
	editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
		return uploadAdapter(loader);
	};
}

const Update = ({ created_at, title, description, update_tools, photo, userName, post_id, detail_id, linkedin, whatsapp, publish, tools }) => {
	let initialState = {
		title: title,
		tools: update_tools,
		text: description,
	};

	const dispatch = useDispatch();
	const { addToast } = useToasts();
	const [edit, setEdit] = useState(false);
	const [update, setUpdate] = useState({ ...initialState });
	const [showConfirm, setShowConfirm] = useState(false);
	const [isFetching, setIsFetching] = useState(false);
	const user = JSON.parse(localStorage.getItem("superuser"));

	const handleCKEditor = (event, editor) => {
		const data = editor.getData();
		setUpdate({ ...update, text: data });
	};

	const handleInputChange = (e) => {
		e.preventDefault();
		const { name, value } = e.target;
		setUpdate({ ...update, [name]: value });
	};

	const handleEditt = () => {
		setEdit(!edit);
	};

	const handleClose = () => {
		setShowConfirm(false);
	};

	const confirmDelete = () => {
		setShowConfirm(true);
	};

	const handleDelete = () => {
		dispatch({
			type: INITIAL_UPDATE_REQUEST,
		});

		const params = {
			post_id: post_id,
			detail_id: detail_id,
		};
		api.post("/admin/delete_update", params).then((response) => {
			addToast(response.message, {
				appearance: "success",
			});

			dispatch({
				type: DELETE_UPDATE_SUCCESS,
				payload: {
					updates: response.posts.updates.data,
				},
			});
			handleClose();
		});
	};

	const handleEditSubmit = () => {
		dispatch({
			type: INITIAL_UPDATE_REQUEST,
		});

		const params = {
			title: update.title,
			text: update.text,
			tools: update.tools,
			post_id: post_id,
			detail_id: detail_id,
		};

		api.post("/admin/update_update", params).then((response) => {
			addToast(response.message, {
				appearance: "success",
			});
			dispatch({
				type: SAVE_UPDATE_SUCCESS,
				payload: {
					updates: response.posts.updates.data,
				},
			});
			setUpdate({ ...initialState });
			setEdit(false);
		});
	};

	const changePublish = (publish) => {
		setIsFetching(true);

		api.post("/admin/updates/publish", { post_id, publish }).then((response) => {
			addToast(response.message, {
				appearance: "success",
			});
			dispatch({
				type: SAVE_UPDATE_SUCCESS,
				payload: {
					updates: response.posts.updates.data,
				},
			});

			setIsFetching(false);
		});
	};

	return (
		<Col className={`${classes.update_list}`}>
			<Row>
				<Col md={2} className={`${classes.date}`}>
					{dateToLocale(created_at)}
				</Col>

				<Col md={9} className={`${classes.content}`}>
					{isFetching ? (
						<Fetching topMargin={"1em"} />
					) : (
						<Col>
							{edit === true && user === 1 ? (
								<>
									<Form>
										<Form.Group>
											<Form.Label>Titulo de la actualizacion</Form.Label>
											<Form.Control
												type="text"
												placeholder="Ej: Nueva Actualización"
												className={`${classes.title_input}`}
												name="title"
												onChange={handleInputChange}
												value={update.title}
											/>
										</Form.Group>
										<Form.Group>
											<CKEditor
												editor={ClassicEditor}
												data={description}
												onChange={handleCKEditor}
												config={{
													licenseKey: "GPL",
													extraPlugins: [uploadPlugin],
													mediaEmbed: {
														previewsInData: true,
													},
												}}
											/>
										</Form.Group>
										<Form.Group>
											<Select
												defaultValue={tools.filter((tool) => update.tools.split(",").includes(String(tool.value)))}
												value={tools.filter((tool) => update.tools.split(",").includes(String(tool.value)))}
												isMulti
												placeholder={<div>Seleccione</div>}
												options={tools || []}
												onChange={(value) => {
													const values_string = value.map((v) => v.value).join(",");
													setUpdate({ ...update, tools: values_string });
												}}
											/>
										</Form.Group>
									</Form>
									<div className={`${classes.ckeditor_buttons}`}>
										<Button className={`${classes.danger_btn}`} onClick={handleEditt}>
											Cancelar
										</Button>
										<Button className={`${classes.success_btn}`} onClick={handleEditSubmit}>
											Guardar
										</Button>
									</div>
								</>
							) : (
								<div onDoubleClick={handleEditt}>
									{user === 1 && (
										<>
											{Number(publish) === 1 ? (
												<CustomBadge className={classes.badge_publish} onClick={() => changePublish(false)} variant="success">
													Publicado
												</CustomBadge>
											) : (
												<CustomBadge className={classes.badge_publish} onClick={() => changePublish(true)} variant="neutral">
													Pendiente
												</CustomBadge>
											)}
										</>
									)}

									<div className={`${classes.update_header}`}>
										<h3>{title}</h3>
										{user === 1 && <CloseIcon className={`${classes.close_icon}`} onClick={confirmDelete} />}
									</div>

									<div
										className={`${classes.base_ckeditor}`}
										dangerouslySetInnerHTML={{
											__html: DOMPurify.sanitize(description, {
												ADD_TAGS: ["iframe"],
											}),
										}}
									/>
								</div>
							)}
						</Col>
					)}

					<Col className={classes.user}>
						<img src={photo || "https://placehold.co/50"} alt="imagen" className={`${classes.image}`} />
						<p>{userName}</p>
						<a href={linkedin} target="_blank" rel="noopener noreferrer">
							<LinkedInSvg className={`${classes.icon}`} />
						</a>

						<a href={whatsapp} target="_blank" rel="noopener noreferrer">
							<WhatsappSvg className={`${classes.icon}`} />
						</a>
					</Col>
				</Col>
			</Row>
			<BasicConfirm
				showConfirm={showConfirm}
				setShowConfirm={setShowConfirm}
				message={`¿Estas seguro de eliminar esta publicación?`}
				handleAction={handleDelete}
			/>
		</Col>
	);
};

export default Update;
